import type { ScopeQuery } from "@app/shared"
import { z } from "zod"
import { http } from "../lib/http"

/**
 * Lot unit enum. Mirrors `lot_unit` pgEnum in `@app/db`. Kept
 * as a string union here so the UI can render it directly without a
 * round-trip.
 */
export const LotUnitSchema = z.enum(["MT", "KG", "BAGS", "CTNS", "BOXES"])

export type LotUnit = z.infer<typeof LotUnitSchema>

/**
 * Mirrors `LotSummaryDto` in `apps/engine/src/lots/dto.ts`. `quantity`
 * arrives as a string because the engine's `numeric` column is
 * canonically stringified by postgres-js to avoid JS number-precision
 * loss on large values.
 */
export const LotSummarySchema = z.object({
	id: z.string(),
	nsbNumber: z.string(),
	verticalId: z.string(),
	verticalName: z.string().nullable(),
	originCountry: z.string(),
	shipmentId: z.string().nullable(),
	quantity: z.string(),
	unit: LotUnitSchema,
})

export const LotDetailSchema = LotSummarySchema.extend({
	originWarehouseId: z.string().nullable(),
	hsCode: z.string().nullable(),
	packingSpec: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
})

const LotListSchema = z.array(LotSummarySchema)

export type LotSummary = z.infer<typeof LotSummarySchema>
export type LotDetail = z.infer<typeof LotDetailSchema>

/** GET /shipments/:shipmentId/lots */
export const listShipmentLots = async (
	shipmentId: string,
	signal?: AbortSignal,
): Promise<LotSummary[]> => {
	const raw = await http<unknown>(
		`/shipments/${encodeURIComponent(shipmentId)}/lots`,
		signal !== undefined ? { signal } : {},
	)
	return LotListSchema.parse(raw)
}

/**
 * GET /lots -- tenant-wide list, latest 100 newest first.
 *
 * Optional `q` is a case-insensitive substring filter against
 * `nsbNumber` + `id` (matches the engine's `LotsService.listForTenant`
 * surface). Empty/whitespace `q` = no filter (same as omitting it).
 * Optional `scope` narrows the list to the app-wide country × vertical
 * filter; `q` + scope are combined into a single query string.
 */
export const listLots = async (
	options: { q?: string; scope?: ScopeQuery; signal?: AbortSignal } = {},
): Promise<LotSummary[]> => {
	const trimmed = options.q?.trim() ?? ""
	const search = new URLSearchParams()
	if (trimmed.length > 0) search.set("q", trimmed)
	if (options.scope?.country) search.set("country", options.scope.country)
	if (options.scope?.vertical) search.set("vertical", options.scope.vertical)
	const qs = search.toString()
	const path = qs ? `/lots?${qs}` : "/lots"
	const raw = await http<unknown>(
		path,
		options.signal !== undefined ? { signal: options.signal } : {},
	)
	return LotListSchema.parse(raw)
}

/**
 * Mirrors `CreateLotInput` on the engine. `tenantId` is bound from
 * the session. `quantity` is a string for full-precision decimals.
 */
export interface CreateLotInput {
	readonly nsbNumber: string
	readonly verticalId: string
	readonly originCountry: string
	readonly quantity: string
	readonly unit: LotUnit
	readonly shipmentId?: string | null
	readonly originWarehouseId?: string | null
	readonly hsCode?: string | null
	readonly packingSpec?: string | null
}

/** POST /lots -- requires `lots.write`. Returns 201 + detail. */
export const createLot = async (
	input: CreateLotInput,
	signal?: AbortSignal,
): Promise<LotDetail> => {
	const raw = await http<unknown>("/lots", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return LotDetailSchema.parse(raw)
}

/** GET /lots/:id -- 404 surfaces as HttpError(status=404). */
export const getLot = async (id: string, signal?: AbortSignal): Promise<LotDetail> => {
	const raw = await http<unknown>(
		`/lots/${encodeURIComponent(id)}`,
		signal !== undefined ? { signal } : {},
	)
	return LotDetailSchema.parse(raw)
}

/**
 * React Query keys for lots. Centralised so detail invalidations
 * triggered from future write mutations land consistently across the
 * shipment-scoped list view.
 */
/** GET /lots/export -- CSV text for the current search + scope. */
export const exportLotsCsv = async (
	options: { q?: string; scope?: ScopeQuery; signal?: AbortSignal } = {},
): Promise<string> => {
	const trimmed = options.q?.trim() ?? ""
	const search = new URLSearchParams()
	if (trimmed.length > 0) search.set("q", trimmed)
	if (options.scope?.country) search.set("country", options.scope.country)
	if (options.scope?.vertical) search.set("vertical", options.scope.vertical)
	const qs = search.toString()
	return http<string>(qs ? `/lots/export?${qs}` : "/lots/export", {
		headers: { Accept: "text/csv" },
		...(options.signal !== undefined ? { signal: options.signal } : {}),
	})
}

/**
 * Body for `PATCH /lots/:id` — send only what changed.
 *
 * Scope fields (`verticalId`, `originCountry`) are accepted by the API but the
 * Helm edit form does not expose them: moving a lot between scopes shifts it
 * between filtered views and deserves a considered surface, not a text box on a
 * detail page. Same call as the shipment edit form makes.
 */
export interface UpdateLotInput {
	nsbNumber?: string
	quantity?: string
	unit?: LotUnit
	hsCode?: string | null
	packingSpec?: string | null
}

/** PATCH /lots/:id */
export const updateLot = async (id: string, input: UpdateLotInput): Promise<LotDetail> => {
	const raw = await http<unknown>(`/lots/${encodeURIComponent(id)}`, {
		method: "PATCH",
		json: input,
	})
	return LotDetailSchema.parse(raw)
}

export const lotsQueryKeys = {
	all: ["lots"] as const,
	// Scope segments are appended only when set, so `list()` stays a valid
	// invalidation prefix for every scoped/q-filtered variant.
	list: (q?: string, scope?: ScopeQuery): readonly string[] => {
		const trimmed = q?.trim() ?? ""
		const key: string[] = [...lotsQueryKeys.all, "list", "tenant"]
		if (trimmed.length > 0) key.push("q", trimmed)
		if (scope?.country) key.push(scope.country)
		if (scope?.vertical) key.push(scope.vertical)
		return key
	},
	listByShipment: (shipmentId: string) =>
		[...lotsQueryKeys.all, "list", "shipment", shipmentId] as const,
	detail: (id: string) => [...lotsQueryKeys.all, "detail", id] as const,
}
